import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity, ArrowDownRight, ArrowUpRight, BellRing, LineChart, Send, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import CopyAddress from "@/components/whale/CopyAddress";
import { useUserCount } from "@/components/whale/Header";

const FEATURES = [
  { icon: ArrowDownRight, color: "#FF3D00", title: "Wallet → Exchange", text: "Whales moving coins onto Binance, Coinbase, Kraken, OKX & more — early sell-pressure signal." },
  { icon: ArrowUpRight, color: "#00E676", title: "Exchange → Wallet", text: "Big withdrawals to private wallets — classic accumulation footprint." },
  { icon: LineChart, color: "#00E5FF", title: "TradingView charts", text: "Every whale flow plotted as markers on live candles, plus the official TradingView chart." },
  { icon: Send, color: "#FFB300", title: "Telegram + browser alerts", text: "$1M+, $10M+ or your own threshold — pinged the moment it hits the chain." },
];

const TAPE = [
  { d: "INFLOW", c: "#FF3D00", a: "1,250 BTC", u: "$103.7M", f: "unknown", t: "Binance" },
  { d: "OUTFLOW", c: "#00E676", a: "42,000 ETH", u: "$105.0M", f: "Coinbase", t: "unknown" },
  { d: "INFLOW", c: "#FF3D00", a: "38M XRP", u: "$52.8M", f: "unknown", t: "Bitstamp" },
  { d: "OUTFLOW", c: "#00E676", a: "25M USDC", u: "$25.0M", f: "Kraken", t: "unknown" },
];

export default function Landing() {
  const [info, setInfo] = useState(null);
  useEffect(() => { axios.get(`${API}/payments/info`).then((r) => setInfo(r.data)); }, []);
  const free = info?.enabled === false;
  const users = useUserCount();

  return (
    <div className="terminal-bg">
      <nav className="flex items-center justify-between px-5 py-4 lg:px-12">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md border border-[#2E3C54] bg-[#0F141C]">
            <Activity className="h-4 w-4 text-[#00E5FF]" />
          </div>
          <span className="font-display text-lg font-bold">WHALE<span className="text-[#00E5FF]">/</span>FLOW</span>
        </div>
        <div className="flex gap-2">
          <Link to="/login" data-testid="nav-login-link" className="rounded-md px-4 py-2 text-sm text-slate-300 transition-colors hover:text-white">Log in</Link>
          <Link to="/register" data-testid="nav-register-link" className="rounded-md bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-[#080B10] transition-opacity hover:opacity-90">Get access</Link>
        </div>
      </nav>

      <section className="grid gap-12 px-5 pb-16 pt-10 lg:grid-cols-12 lg:px-12 lg:pt-20">
        <div className="rise lg:col-span-7">
          <p className="overline mb-4">on-chain exchange flow surveillance</p>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-headline">
            See the whales <span className="text-[#FF3D00]">before</span> the candle moves.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
            Real-time alerts when big holders move BTC, ETH, USDT, USDC, XRP, SOL or QNT between private wallets and exchanges — plotted right on your TradingView chart.
          </p>
          {users != null && (
            <p className="mt-5 flex items-center gap-2 font-mono text-sm text-slate-300" data-testid="landing-user-count">
              <span className="live-dot" /> <span className="font-bold text-[#00E5FF]">{users.total.toLocaleString()}</span> traders ·{" "}
              <span className="font-bold text-[#00E5FF]">{users.visitors.toLocaleString()}</span> visitors watching the whales
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-get-access-button" className="rounded-md bg-[#00E5FF] px-6 py-3 font-semibold text-[#080B10] transition-opacity hover:opacity-90">
              {free ? "Get free access" : `Get lifetime access — ${info ? `${info.amount} USDC` : "…"}`}
            </Link>
            {!free && <a href="#pay" className="rounded-md border border-[#2E3C54] px-6 py-3 text-slate-200 transition-colors hover:border-[#00E5FF]">How to pay</a>}
          </div>
        </div>
        <div className="panel rise overflow-hidden lg:col-span-5" style={{ animationDelay: "120ms" }}>
          <div className="flex items-center justify-between border-b border-[#1E2738] p-3">
            <span className="font-display text-sm font-semibold">Live whale tape</span>
            <span className="flex items-center gap-2 font-mono text-[10px] text-slate-500"><span className="live-dot" />example</span>
          </div>
          {TAPE.map((x, i) => (
            <div key={x.a} className="feed-item border-b border-[#1E2738] px-3 py-3" style={{ borderLeft: `3px solid ${x.c}`, animationDelay: `${200 + i * 120}ms` }}>
              <div className="flex justify-between font-mono text-[10px] font-bold" style={{ color: x.c }}><span>{x.d}</span></div>
              <div className="mt-1 flex justify-between font-mono text-sm"><span>{x.a}</span><span className="font-bold text-[#FFD700]">{x.u}</span></div>
              <div className="mt-1 font-mono text-[11px] text-slate-500">{x.f} → {x.t}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-4 px-5 pb-20 sm:grid-cols-2 lg:grid-cols-4 lg:px-12">
        {FEATURES.map((f) => (
          <div key={f.title} className="panel p-5">
            <f.icon className="h-5 w-5" style={{ color: f.color }} />
            <h3 className="mt-4 font-display font-semibold">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.text}</p>
          </div>
        ))}
      </section>

      {free ? (
        <section className="px-5 pb-24 lg:px-12" data-testid="free-access-banner">
          <div className="panel flex flex-wrap items-center justify-between gap-4 p-6 lg:p-10">
            <div>
              <p className="overline">limited time</p>
              <h2 className="mt-2 font-display text-3xl font-bold">Full access is <span className="text-[#00E676]">free</span> right now</h2>
              <p className="mt-2 text-slate-400">Create an account and open the live terminal instantly.</p>
            </div>
            <Link to="/register" data-testid="free-access-cta" className="rounded-md bg-[#00E5FF] px-6 py-3 font-semibold text-[#080B10] transition-opacity hover:opacity-90">Start free</Link>
          </div>
        </section>
      ) : (
      <section id="pay" className="px-5 pb-24 lg:px-12" data-testid="payment-instructions">
        <div className="panel grid gap-10 p-6 lg:grid-cols-12 lg:p-10">
          <div className="lg:col-span-5">
            <p className="overline">pricing</p>
            <div className="mt-3 font-mono text-5xl font-bold" data-testid="landing-price">{info ? info.amount : "…"} <span className="text-2xl text-slate-400">USDC</span></div>
            <p className="mt-2 text-slate-400">One-time payment · lifetime access · Solana network</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {["All 7 coins, live 24/7", "Telegram, browser & sound alerts", "Whale markers on TradingView-style charts", "Custom thresholds & directions"].map((x) => (
                <li key={x} className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#00E676]" />{x}</li>
              ))}
            </ul>
          </div>
          <div className="space-y-5 lg:col-span-7">
            <Step n="1" title="Create your account">Sign up with email and password.</Step>
            <Step n="2" title={`Send your exact amount (≈${info?.amount ?? "…"} USDC) on Solana`}>
              After sign-up you get a unique amount like {info?.amount ?? 100}.4721 USDC that ties the payment to your account. <span className="text-[#FFB300]">Only USDC on the Solana network</span> — other tokens or networks will be lost.
              {info && <CopyAddress address={info.address} testid="landing-pay-address" />}
            </Step>
            <Step n="3" title="Paste your transaction signature">We verify it on-chain instantly and unlock the terminal.</Step>
            <Link to="/register" data-testid="pay-section-cta" className="inline-flex items-center gap-2 rounded-md bg-[#00E5FF] px-6 py-3 font-semibold text-[#080B10] transition-opacity hover:opacity-90">
              <BellRing className="h-4 w-4" /> Start now
            </Link>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#2E3C54] font-mono text-sm text-[#00E5FF]">{n}</div>
    <div className="min-w-0 flex-1">
      <h4 className="font-display font-semibold">{title}</h4>
      <div className="mt-1 text-sm text-slate-400">{children}</div>
    </div>
  </div>
);
